import { h, a as getRenderingRef, f as forceUpdate, r as registerInstance, c as createEvent, H as Host } from './index-5aea3fea.js';
import { i as i18nProvider, l as loadTranslations } from './loadTranslations-f63b1e5d.js';
import { g as getSoftwares, a as getSoftwareTypes, S as SOFTWARE_TYPES, b as getSoftwareModulesByType, c as createRequestData } from './createCase-7e2d933f.js';
import { b as getAuthClient, c as authClientName, g as getEnvConfig, A as ApiClient, s as setEnv, a as getPublicApiClient } from './publicApiClient-8f578e40.js';

/**
 * This can be used like a Fragment in React. https://legacy.reactjs.org/docs/fragments.html
 */
const Fragment = (_props, children) => [...children];

const FormSteps = ({ multipleSteps }, children) => {
    if (!multipleSteps)
        return children;
    return h("ux-form-steps", null, children);
};
const FormStep = ({ multipleSteps }, children) => {
    if (!multipleSteps)
        return children;
    return h("ux-form-step", null, children);
};
const StepNavigation = ({ t, multipleSteps, currentStep }) => multipleSteps ? (h("div", { class: "ux-u-margin-top-40 ux-u-margin-bottom-40", style: { "min-height": "87px" } },
    h("ux-steps", null,
        h("ux-steps-item", { "data-ux-form-steps-prev": currentStep === 2 ? true : undefined, number: 1, current: currentStep === 1, done: currentStep === 2 }, t("headline.caseDetails")),
        h("ux-steps-item", { number: 2, current: currentStep === 2, "data-ux-form-steps-next": currentStep === 1 ? true : undefined }, t("headline.contactData"))))) : null;
const StepNext = ({ t, multipleSteps, onCancel }) => multipleSteps ? (h("div", { class: "ux-row" },
    h("div", { class: "ux-col-12 ux-u-margin-bottom-20" },
        h("div", { class: "ux-text-normal ux-text-normal--light-gray" },
            h("p", null, t("validation.requiredFieldsExplanation")))),
    h("div", { class: "ux-col-12" },
        h("div", { class: "ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse" },
            h("ux-button", { "data-ux-form-steps-next": true, variant: "primary" }, t("button.next")),
            h("ux-button", { type: "button", onClick: () => onCancel() }, t("button.cancel")))))) : null;
const StepBack = ({ t, multipleSteps }) => multipleSteps ? (h("ux-button", { "data-ux-form-steps-prev": true, type: "button" }, t("button.back"))) : null;

const appendToMap = (map, propName, value) => {
    const items = map.get(propName);
    if (!items) {
        map.set(propName, [value]);
    }
    else if (!items.includes(value)) {
        items.push(value);
    }
};
const debounce = (fn, ms) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = 0;
            fn(...args);
        }, ms);
    };
};

/**
 * Check if a possible element isConnected.
 * The property might not be there, so we check for it.
 *
 * We want it to return true if isConnected is not a property,
 * otherwise we would remove these elements and would not update.
 *
 * Better leak in Edge than to be useless.
 */
const isConnected = (maybeElement) => !('isConnected' in maybeElement) || maybeElement.isConnected;
const cleanupElements = debounce((map) => {
    for (let key of map.keys()) {
        map.set(key, map.get(key).filter(isConnected));
    }
}, 2_000);
const stencilSubscription = () => {
    if (typeof getRenderingRef !== 'function') {
        // If we are not in a stencil project, we do nothing.
        // This function is not really exported by @stencil/core.
        return {};
    }
    const elmsToUpdate = new Map();
    return {
        dispose: () => elmsToUpdate.clear(),
        get: (propName) => {
            const elm = getRenderingRef();
            if (elm) {
                appendToMap(elmsToUpdate, propName, elm);
            }
        },
        set: (propName) => {
            const elements = elmsToUpdate.get(propName);
            if (elements) {
                elmsToUpdate.set(propName, elements.filter(forceUpdate));
            }
            cleanupElements(elmsToUpdate);
        },
        reset: () => {
            elmsToUpdate.forEach((elms) => elms.forEach(forceUpdate));
            cleanupElements(elmsToUpdate);
        },
    };
};

const unwrap = (val) => (typeof val === 'function' ? val() : val);
const createObservableMap = (defaultState, shouldUpdate = (a, b) => a !== b) => {
    const unwrappedState = unwrap(defaultState);
    let states = new Map(Object.entries(unwrappedState ?? {}));
    const handlers = {
        dispose: [],
        get: [],
        set: [],
        reset: [],
    };
    const reset = () => {
        // When resetting the state, the default state may be a function - unwrap it to invoke it.
        // otherwise, the state won't be properly reset
        states = new Map(Object.entries(unwrap(defaultState) ?? {}));
        handlers.reset.forEach((cb) => cb());
    };
    const dispose = () => {
        // Call first dispose as resetting the state would
        // cause less updates ;)
        handlers.dispose.forEach((cb) => cb());
        reset();
    };
    const get = (propName) => {
        handlers.get.forEach((cb) => cb(propName));
        return states.get(propName);
    };
    const set = (propName, value) => {
        const oldValue = states.get(propName);
        if (shouldUpdate(value, oldValue, propName)) {
            states.set(propName, value);
            handlers.set.forEach((cb) => cb(propName, value, oldValue));
        }
    };
    const state = (typeof Proxy === 'undefined'
        ? {}
        : new Proxy(unwrappedState, {
            get(_, propName) {
                return get(propName);
            },
            ownKeys(_) {
                return Array.from(states.keys());
            },
            getOwnPropertyDescriptor() {
                return {
                    enumerable: true,
                    configurable: true,
                };
            },
            has(_, propName) {
                return states.has(propName);
            },
            set(_, propName, value) {
                set(propName, value);
                return true;
            },
        }));
    const on = (eventName, callback) => {
        handlers[eventName].push(callback);
        return () => {
            removeFromArray(handlers[eventName], callback);
        };
    };
    const onChange = (propName, cb) => {
        const unSet = on('set', (key, newValue) => {
            if (key === propName) {
                cb(newValue);
            }
        });
        // We need to unwrap the defaultState because it might be a function.
        // Otherwise we might not be sending the right reset value.
        const unReset = on('reset', () => cb(unwrap(defaultState)[propName]));
        return () => {
            unSet();
            unReset();
        };
    };
    const use = (...subscriptions) => {
        const unsubs = subscriptions.reduce((unsubs, subscription) => {
            if (subscription.set) {
                unsubs.push(on('set', subscription.set));
            }
            if (subscription.get) {
                unsubs.push(on('get', subscription.get));
            }
            if (subscription.reset) {
                unsubs.push(on('reset', subscription.reset));
            }
            if (subscription.dispose) {
                unsubs.push(on('dispose', subscription.dispose));
            }
            return unsubs;
        }, []);
        return () => unsubs.forEach((unsub) => unsub());
    };
    const forceUpdate = (key) => {
        const oldValue = states.get(key);
        handlers.set.forEach((cb) => cb(key, oldValue, oldValue));
    };
    return {
        state,
        get,
        set,
        on,
        onChange,
        use,
        dispose,
        reset,
        forceUpdate,
    };
};
const removeFromArray = (array, item) => {
    const index = array.indexOf(item);
    if (index >= 0) {
        array[index] = array[array.length - 1];
        array.length--;
    }
};

const createStore = (defaultState, shouldUpdate) => {
    const map = createObservableMap(defaultState, shouldUpdate);
    map.use(stencilSubscription());
    return map;
};

/**
 * Decodes a JWT token to an object
 * implementation from https://stackoverflow.com/questions/38552003/how-to-decode-jwt-token-in-javascript-without-using-a-library
 * @param token JWT token
 * @returns object
 */
function decodeJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""));
    return JSON.parse(jsonPayload);
}

const getAssets = async (apiClient) => {
    const idToken = getAuthClient(authClientName).getIdToken();
    const decoded = decodeJwt(idToken);
    const { customerNumber } = decoded;
    return apiClient
        .request(`/asset-management/1.0/core/assets/parameters?customerOrgId=${customerNumber}&isCustomOnly=false&isRootAsset=true`, {
        method: "GET",
        mockResponse
    })
        .then((response) => {
        if (response.assets) {
            return response.assets;
        }
        return Promise.reject("No assets found");
    });
};
const mockResponse = {
    assets: [
        {
            assetId: "A0660A0576",
            assetName: "Quicksharp",
            technologyId: "PUNCHING"
        },
        {
            assetId: "LS00093708",
            assetName: "HL 54P",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "E0551A0120",
            assetName: "HL 101P",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "LS00009160",
            assetName: "HL 2006D",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "LS00093844",
            assetName: "HL 2006D",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "N0053A0020",
            assetName: "SortMaster (AL28)",
            technologyId: "AUTOMATION"
        },
        {
            assetId: "C0200I0005",
            assetName: "Toolshuttle",
            technologyId: "AUTOMATION"
        },
        {
            assetId: "883850",
            assetName: "TRBV85SX",
            technologyId: "BENDING"
        },
        {
            assetId: "B0501A0185",
            assetName: "TruBend 5050",
            technologyId: "BENDING"
        },
        {
            assetId: "B1503I0001",
            assetName: "TruBend 5085 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1504I0151",
            assetName: "TruBend 5130 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1505I0280",
            assetName: "TruBend 5170 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1505I0637",
            assetName: "TruBend 5170 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1507I0001",
            assetName: "TruBend 5230 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B1509I0001",
            assetName: "TruBend 5320 (B23)",
            technologyId: "BENDING"
        },
        {
            assetId: "B0702A2028",
            assetName: "TruBend 7036",
            technologyId: "BENDING"
        },
        {
            assetId: "B0702A2031",
            assetName: "TruBend 7036",
            technologyId: "BENDING"
        },
        {
            assetId: "B1703I0086",
            assetName: "TruBend 7050 (B28)",
            technologyId: "BENDING"
        },
        {
            assetId: "E0953A0235",
            assetName: "TruDisk 2001 (4C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E0953A0078",
            assetName: "TruDisk 4002 (4C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E1153A0485",
            assetName: "TruDisk 4002 (4C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E1153A0426",
            assetName: "TruDisk 6602 (4 C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E0953A0227",
            assetName: "TruDisk 8002 (2C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "E1053A0162",
            assetName: "TruDisk  3001 (4 C)",
            technologyId: "DISKLASER"
        },
        {
            assetId: "A0221E0833",
            assetName: "TruLaser 3030 fiber (L49)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A0221E1065",
            assetName: "TruLaser 3030 fiber (L49)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A2221E0146",
            assetName: "TruLaser 3030 fiber (L95)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A3231E0025",
            assetName: "TruLaser 5030 fiber (L76)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A3231E0518",
            assetName: "TruLaser 5030 fiber (L76)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "S2321M0144",
            assetName: "TruLaser Cell 3000 (L34)",
            technologyId: "WELDING"
        },
        {
            assetId: "S0541C0287",
            assetName: "TruLaser Cell 7040 (L31) fiber",
            technologyId: "3DLASERCUTTING"
        },
        {
            assetId: "S0541C0328",
            assetName: "TruLaser Cell 7040 (L31) fiber",
            technologyId: "3DLASERCUTTING"
        },
        {
            assetId: "A0280E0038",
            assetName: "TruLaser Center 7030 (L26)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A0280E0127",
            assetName: "TruLaser Center 7030 (L26)",
            technologyId: "2DLASERCUTTING"
        },
        {
            assetId: "A0455A0024",
            assetName: "TruLaser Robot 5020",
            technologyId: "LASERTUBECUTTING"
        },
        {
            assetId: "A0455A0037",
            assetName: "TruLaser Robot 5020",
            technologyId: "LASERTUBECUTTING"
        },
        {
            assetId: "A1441D0079",
            assetName: "TruLaser Tube 7000 fiber",
            technologyId: "LASERTUBECUTTING"
        },
        {
            assetId: "A0150A0160",
            assetName: "TruMatic 7000",
            technologyId: "COMBINED"
        },
        {
            assetId: "A1150D0126",
            assetName: "TruMatic 7000 (K08) Medium format",
            technologyId: "COMBINED"
        },
        {
            assetId: "A0460A0004",
            assetName: "TrumaWeld L 1000",
            technologyId: "WELDING"
        },
        {
            assetId: "A0420A0232",
            assetName: "TRUMPF Lasercell 1005",
            technologyId: "3DLASERCUTTING"
        },
        {
            assetId: "S0711Q0095",
            assetName: "TruPrint 1000 (G01)",
            technologyId: "3DPRINTING"
        },
        {
            assetId: "E0851A0036",
            assetName: "TruPulse 203",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "E0851A0048",
            assetName: "TruPulse 556",
            technologyId: "PULSEDLASER"
        },
        {
            assetId: "E0652A0387",
            assetName: "VectorMark Compact 1",
            technologyId: "MARKINGLASER"
        }
    ]
};

const getNotificationTypes = async (apiClient) => {
    return apiClient.request("/servicecall/getnotificationtypes", {
        mockResponse: mockNotificationTypes
    });
};
const NOTIFICATION_TYPES = {
    MACHINE_STOP: 20,
    FAULT_WITHOUT_STOP: 10,
    PROGRAMMING_SOFTWARE: -100,
    CLARIFICATION_SPARE_PARTS: 50,
    OTHER: 40,
    MAINTENANCE_REQUEST: 120,
    SPARE_PARTS_ORDER: 140,
    APPLICATION_ISSUES: 80
};
const mockNotificationTypes = [
    {
        Id: NOTIFICATION_TYPES.MACHINE_STOP,
        LabelKey: "Maschinenstillstand"
    },
    {
        Id: NOTIFICATION_TYPES.FAULT_WITHOUT_STOP,
        LabelKey: "Störung ohne Stillstand"
    },
    {
        Id: NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE,
        LabelKey: "Programmierung / Software"
    },
    {
        Id: NOTIFICATION_TYPES.CLARIFICATION_SPARE_PARTS,
        LabelKey: "Klärung Ersatzteile"
    },
    {
        Id: NOTIFICATION_TYPES.OTHER,
        LabelKey: "Sonstiges"
    },
    {
        Id: NOTIFICATION_TYPES.MAINTENANCE_REQUEST,
        LabelKey: "Wartungsanfrage"
    },
    {
        Id: NOTIFICATION_TYPES.SPARE_PARTS_ORDER,
        LabelKey: "Bestellung Ersatzteile"
    },
    {
        Id: NOTIFICATION_TYPES.APPLICATION_ISSUES,
        LabelKey: "Applikationsthemen"
    }
];

const createApiStore = () => {
    const store = createStore({
        machinesLoading: true,
        machines: [],
        softwaresLoading: true,
        softwares: [],
        softwareTypes: [],
        notificationTypesLoading: true,
        notificationTypes: [],
        loading: true
    });
    store.onChange("machinesLoading", () => updateLoading());
    store.onChange("softwaresLoading", () => updateLoading());
    store.onChange("notificationTypesLoading", () => updateLoading());
    function updateLoading() {
        store.state.loading = store.state.machinesLoading || store.state.softwaresLoading || store.state.notificationTypesLoading;
    }
    return store;
};
const loadFormDataFromPublicApi = async (apiStore, apiClient) => {
    apiStore.state.softwaresLoading = true;
    apiStore.state.notificationTypesLoading = true;
    getSoftwares(apiClient).then((softwares) => {
        apiStore.state.softwares = softwares;
        apiStore.state.softwareTypes = getSoftwareTypes(softwares);
        apiStore.state.softwaresLoading = false;
    });
    getNotificationTypes(apiClient).then((notificationTypes) => {
        apiStore.state.notificationTypes = notificationTypes;
        apiStore.state.notificationTypesLoading = false;
    });
};
const loadFormDataFromAssetManagementApi = async (apiStore, apiClient) => {
    apiStore.state.machinesLoading = true;
    getAssets(apiClient).then((machines) => {
        apiStore.state.machines = machines;
        apiStore.state.machinesLoading = false;
    });
};

const authClientServiceCalls = getAuthClient(authClientName);
const createAuthStore = () => {
    const store = createStore({
        isAuthenticating: false,
        authError: null
    });
    return store;
};
const SilentAuth = ({ disabled, config, authStore }) => {
    if (disabled || config.useMockData)
        return;
    const onAuthError = (e) => {
        authStore.state.authError = e.detail.error;
        if (authStore.state.authError !== null) {
            authStore.state.isAuthenticating = false;
        }
    };
    return (h("ux-u-silent-auth", { "client-name": authClientName, "client-id": config.authClientId, issuer: config.authIssuer, scope: config === null || config === void 0 ? void 0 : config.authScope, onUxAuth: (e) => onAuthError(e) }));
};
/**
 * utility to execute a function as soon as the user is authenticated
 * @param fn callback to be executed as soon as the user is authenticated
 * @returns a function to unsubscribe
 */
function whenAuthenticated(callback) {
    const { useMockData } = getEnvConfig();
    if (authClientServiceCalls.getAccessToken() !== null || useMockData) {
        callback();
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => { };
    }
    return authClientServiceCalls.subscribe(callback);
}

const APPLICATION_IDS = {
    WELDING: 1,
    PLASTIC: 2,
    CUTTING: 3,
    HARDENING: 4,
    MARKING: 5,
    OTHER: 6,
    FIXED_OPTICS_BEO: 7,
    PFO20_33: 8,
    PFO3D: 9,
    I_PFO: 10,
    OTHER_OPTICS: 11,
    WELD_DEPTH: 12,
    HARDNESS: 13,
    CRACKING: 14,
    BUBBLE_SPLATTER: 15,
    PROCESS_DATA: 16,
    OTHER_WELDING: 17
};
const applicationsLaser = [
    {
        labelKey: "welding",
        id: APPLICATION_IDS.WELDING
    },
    {
        labelKey: "plastic",
        id: APPLICATION_IDS.PLASTIC
    },
    {
        labelKey: "cutting",
        id: APPLICATION_IDS.CUTTING
    },
    {
        labelKey: "hardening",
        id: APPLICATION_IDS.HARDENING
    },
    {
        labelKey: "marking",
        id: APPLICATION_IDS.MARKING
    },
    {
        labelKey: "other",
        id: APPLICATION_IDS.OTHER
    }
];
const applicationsOptics = [
    {
        labelKey: "fixedOpticsBEO",
        id: APPLICATION_IDS.FIXED_OPTICS_BEO
    },
    {
        labelKey: "PFO20/33",
        id: APPLICATION_IDS.PFO20_33
    },
    {
        labelKey: "PFO3D",
        id: APPLICATION_IDS.PFO3D
    },
    {
        labelKey: "I-PFO",
        id: APPLICATION_IDS.I_PFO
    },
    {
        labelKey: "other",
        id: APPLICATION_IDS.OTHER_OPTICS
    }
];
const applicationsWelding = [
    {
        labelKey: "weldDepth",
        id: APPLICATION_IDS.WELD_DEPTH
    },
    {
        labelKey: "hardness",
        id: APPLICATION_IDS.HARDNESS
    },
    {
        labelKey: "cracking",
        id: APPLICATION_IDS.CRACKING
    },
    {
        labelKey: "bubbleSplatter",
        id: APPLICATION_IDS.BUBBLE_SPLATTER
    },
    {
        labelKey: "processData",
        id: APPLICATION_IDS.PROCESS_DATA
    },
    {
        labelKey: "other",
        id: APPLICATION_IDS.OTHER_WELDING
    }
];

const hideMachinesForNotificationTypes = [NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE];
const showErrorNumbersForNotificationTypes = [
    null,
    NOTIFICATION_TYPES.APPLICATION_ISSUES,
    NOTIFICATION_TYPES.MACHINE_STOP,
    NOTIFICATION_TYPES.FAULT_WITHOUT_STOP
];
const hideErrorNumbersForTechnologies = ["ELECTRONICS"];
const showSoftwareFieldsForNotificationTypes = [NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE];
const showSoftwareModulesForSoftwareTypes = [SOFTWARE_TYPES.TRUTOPS, SOFTWARE_TYPES.TRUTOPS_FAB, SOFTWARE_TYPES.TRUTOPS_BOOST];
const showApplicationIssuesForNotificationTypes = [NOTIFICATION_TYPES.APPLICATION_ISSUES];
const showApplicationIssuesForTechnologies = [
    "PULSEDLASER",
    "SHORTPULSEDLASER",
    "DISKLASER",
    "FIBERLASER",
    "DIODELASER",
    "MARKINGLASER"
];
const showWeldingApplicationFieldsForApplication = [APPLICATION_IDS.WELDING];
const createFormStore = () => {
    const store = createStore({
        selectedNotificationType: null,
        selectedSoftwareType: null,
        selectedApplication: null,
        formIsSubmitted: false,
        selectedMachineTechnology: null,
        showErrorNumbers: true,
        showApplications: false,
        showSoftwareModules: false,
        showMachines: true,
        showSoftwareFields: false,
        showWeldingApplicationFields: false
    });
    store.onChange("selectedNotificationType", (selectedNotificationType) => {
        store.state.showMachines = !hideMachinesForNotificationTypes.includes(selectedNotificationType);
        store.state.showSoftwareFields = showSoftwareFieldsForNotificationTypes.includes(selectedNotificationType);
        determineShowErrorNumbers();
        determineShowApplications();
    });
    store.onChange("selectedMachineTechnology", () => {
        determineShowErrorNumbers();
        determineShowApplications();
    });
    store.onChange("selectedApplication", (selectedApplication) => {
        store.state.showWeldingApplicationFields = showWeldingApplicationFieldsForApplication.includes(selectedApplication);
    });
    store.onChange("selectedSoftwareType", (selectedSoftwareType) => {
        store.state.showSoftwareModules = showSoftwareModulesForSoftwareTypes.includes(selectedSoftwareType);
    });
    function determineShowApplications() {
        const { selectedNotificationType, selectedMachineTechnology } = store.state;
        store.state.showApplications =
            showApplicationIssuesForNotificationTypes.includes(selectedNotificationType) &&
                showApplicationIssuesForTechnologies.includes(selectedMachineTechnology);
    }
    function determineShowErrorNumbers() {
        const { selectedNotificationType, selectedMachineTechnology } = store.state;
        store.state.showErrorNumbers =
            showErrorNumbersForNotificationTypes.includes(selectedNotificationType) &&
                !hideErrorNumbersForTechnologies.includes(selectedMachineTechnology);
    }
    return store;
};

const ErrorNumberInput = ({ t, prefillData }) => {
    var _a, _b, _c, _d;
    return (h("div", { class: "ux-u-margin-bottom-20" },
        h("label", { class: "ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase" }, t("field.errorNumber.label")),
        h("service-calls-dynamic-input", { "add-text": t("field.errorNumber.addAnotherNumber"), "input-name": "errorNumber1", prefilledValues: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _a === void 0 ? void 0 : _a.errorNumbers) || [], "init-items": ((_c = (_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _b === void 0 ? void 0 : _b.errorNumbers) === null || _c === void 0 ? void 0 : _c.length) || 1, "min-items": "1", "max-items": "6", disabled: !!((_d = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _d === void 0 ? void 0 : _d.errorNumbers) },
            h("div", { class: "mf-create-service-case__help-block", slot: "info" },
                h("p", { class: "ux-text-tiny" }, t("field.errorNumber.explanation", {
                    example1: "0000FC00",
                    example2: "1.10.58102409",
                    example3: "6000"
                }))))));
};

const SALUTATION = {
    FEMALE: 1,
    MALE: 2
};

const getLanguageFromLocale = (locale) => {
    if (!locale.includes("-")) {
        return locale;
    }
    return locale.split("-")[0];
};
const getCountryFromLocale = (locale) => {
    if (!locale.includes("-")) {
        return null;
    }
    return locale.split("-")[1];
};

const SalutationInput = ({ t, prefillData }) => {
    var _a, _b, _c;
    return (h("ux-select", { label: t("field.salutation.label"), required: true, name: "salutation" },
        h("option", { value: "", selected: typeof ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.salutation) !== "number", disabled: true }, t("select.placeholder")),
        h("option", { value: SALUTATION.FEMALE, selected: ((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _b === void 0 ? void 0 : _b.salutation) === SALUTATION.FEMALE }, t("field.salutation.female")),
        h("option", { value: SALUTATION.MALE, selected: ((_c = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _c === void 0 ? void 0 : _c.salutation) === SALUTATION.MALE }, t("field.salutation.male"))));
};
const FirstNameInput = ({ t, prefillData }) => {
    var _a;
    return (h("ux-input-field", { "data-validation-required--msg": t("validation.requiredField"), "data-validation-length--max": "45", "data-validation-length--msg": t("validation.maxAllowedCharacters", {
            number: 45
        }), label: t("field.firstName.label"), name: "firstName", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.firstName) || "", required: true }));
};
const LastNameInput = ({ t, prefillData }) => {
    var _a;
    return (h("ux-input-field", { "data-validation-required--msg": t("validation.requiredField"), "data-validation-length--max": "45", "data-validation-length--msg": t("validation.maxAllowedCharacters", {
            number: 45
        }), label: t("field.lastName.label"), name: "lastName", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.lastName) || "", required: true }));
};
const PhoneInput = ({ t, prefillData, locale }) => {
    var _a;
    return (h("ux-phone-input", { label: t("field.phone.label"), language: getLanguageFromLocale(locale), country: getCountryFromLocale(locale), "data-validation-invalid--msg": t("validation.invalidPhoneNumber"), "data-validation-required--msg": t("validation.requiredField"), name: "phone", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.phone) || "", required: true }));
};
const EmailInput = ({ t, prefillData }) => {
    var _a;
    return (h("ux-input-field", { type: "email", required: true, "data-validation-required--msg": t("validation.requiredField"), "data-validation-length--max": "100", "data-validation-length--msg": t("validation.maxAllowedCharacters", {
            number: 100
        }), "data-validation-invalid--msg": t("validation.invalidEmail"), label: "E-Mail", name: "email", value: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.callBackContact) === null || _a === void 0 ? void 0 : _a.email) || "" }));
};

const classifications = ["standstill", "fault", "slightImpairment", "generalInquiry"];

const SoftwareModuleSelect = ({ t, prefillData, formStore, apiStore }) => {
    var _a, _b;
    return (h("ux-select", { required: true, label: t("field.softwareModule.label"), "data-validation-required--msg": t("validation.requiredField"), name: "softwareModule", disabled: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareModule },
        h("option", { value: "", selected: !((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.softwareModule), disabled: true }, t("select.placeholder")),
        getSoftwareModulesByType(apiStore.state.softwares, formStore.state.selectedSoftwareType).map((softwareModule) => {
            var _a;
            return (h("option", { value: softwareModule, selected: softwareModule === ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareModule) }, t(`softwareModule.${softwareModule}`)));
        })));
};
const SoftwareTypeSelect = ({ t, prefillData, formStore, apiStore }) => {
    var _a, _b;
    return (h("ux-select", { required: true, label: t("field.softwareType.label"), name: "softwareType", onUxChange: (event) => {
            formStore.state.selectedSoftwareType = event.detail.value;
        }, disabled: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareType },
        h("option", { value: "", selected: !((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.softwareType), disabled: true }, t("select.placeholder")),
        apiStore.state.softwareTypes.map((softwareType) => {
            var _a;
            return (h("option", { value: softwareType, selected: ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareType) === softwareType }, softwareType));
        })));
};
const SoftwareClassificationSelect = ({ t, prefillData }) => {
    var _a;
    const hasPrefilledClassification = classifications.includes((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareProblemClassification);
    return (h("ux-select", { required: true, label: t("field.softwareProblemClassification.label"), "data-validation-required--msg": t("validation.requiredField"), name: "softwareProblemClassification" },
        h("option", { value: "", selected: !hasPrefilledClassification, disabled: true }, t("select.placeholder")),
        classifications.map((classification) => {
            var _a;
            return (h("option", { value: classification, selected: classification === ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.softwareProblemClassification) }, t(`softwareProblemClassification.${classification}`)));
        })));
};
const InstallationCheckbox = ({ t, prefillData }) => {
    var _a, _b;
    return (h("ux-checkbox", { label: t("field.softwareInstallationProblem.label"), name: "installationProblem", checked: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.isInstallationProblem, disabled: typeof ((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.isInstallationProblem) === "boolean" }));
};
const LicenseCheckbox = ({ t, prefillData }) => {
    var _a, _b;
    return (h("ux-checkbox", { label: t("field.softwareLicenseIssue.label"), name: "licenseIssue", checked: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _a === void 0 ? void 0 : _a.isLicenseIssue, disabled: typeof ((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.softwareData) === null || _b === void 0 ? void 0 : _b.isLicenseIssue) === "boolean" }));
};

const ApplicationSelect = ({ t, formStore }) => (h("ux-select", { required: true, label: t("field.application.label"), "data-validation-required--msg": t("validation.requiredField"), name: "application", onUxChange: (event) => {
        formStore.state.selectedApplication = Number(event.detail.value);
    } },
    h("option", { value: "", selected: true, disabled: true }, t("select.placeholder")),
    applicationsLaser.map((application) => (h("option", { value: application.id }, t(`application.${application.labelKey}`))))));
const ApplicationOpticsSelect = ({ t }) => (h("ux-select", { label: t("field.optics.label"), name: "optics" },
    h("option", { value: "", selected: true, disabled: true }, t("select.placeholder")),
    applicationsOptics.map((application) => (h("option", { value: application.id }, t(`application.${application.labelKey}`))))));
const ApplicationIssueSelect = ({ t }) => (h("ux-select", { label: t("field.applicationIssue.label"), name: "applicationIssue" },
    h("option", { value: "", selected: true, disabled: true }, t("select.placeholder")),
    applicationsWelding.map((application) => (h("option", { value: application.id }, t(`application.${application.labelKey}`))))));

const MachineSelect = ({ t, prefillData, apiStore, formStore }) => {
    var _a, _b;
    const { machines } = apiStore.state;
    return (h("ux-select", { required: true, label: t("field.machine.label"), "data-validation-required--msg": t("validation.requiredField"), name: "productNo", onUxChange: (event) => {
            const machine = machines.find(({ assetId }) => assetId === event.detail.value);
            if (machine) {
                formStore.state.selectedMachineTechnology = machine.technologyId;
                return;
            }
            // eslint-disable-next-line no-console
            console.error(`Machine with id ${event.detail.value} not found`);
        }, disabled: (_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _a === void 0 ? void 0 : _a.assetId },
        h("option", { value: "", selected: !((_b = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _b === void 0 ? void 0 : _b.assetId), disabled: true }, t("select.placeholder")),
        machines.map((machine) => {
            var _a;
            return (h("option", { key: machine.assetId, value: machine.assetId, selected: machine.assetId === ((_a = prefillData === null || prefillData === void 0 ? void 0 : prefillData.productData) === null || _a === void 0 ? void 0 : _a.assetId) },
                machine.assetName,
                " (",
                machine.assetId,
                ")"));
        })));
};

const NotificationTypeSelect = ({ t, prefillData, apiStore, formStore }) => {
    const { notificationTypes } = apiStore.state;
    const { selectedNotificationType } = formStore.state;
    return (h("ux-select", { required: true, label: t("field.reason.label"), "data-validation-required--msg": t("validation.requiredField"), name: "notificationType", onUxChange: (event) => {
            formStore.state.selectedNotificationType = Number(event.detail.value);
        }, disabled: prefillData === null || prefillData === void 0 ? void 0 : prefillData.notificationType },
        h("option", { value: "", selected: !(prefillData === null || prefillData === void 0 ? void 0 : prefillData.notificationType), disabled: true }, t("select.placeholder")),
        notificationTypes.map((notificationType) => (h("option", { key: String(notificationType.LabelKey), value: notificationType.Id, selected: notificationType.Id === selectedNotificationType }, t(`notificationType.${notificationType.LabelKey}`))))));
};

const assetManagementApiClients = {};
const getAssetManagementApiClient = (config) => {
    const { assetManagementApiBaseUrl, authIssuer, authScope, authClientId } = config;
    const key = `${assetManagementApiBaseUrl}-${authIssuer}-${authScope}-${authClientId}`;
    if (!assetManagementApiClients[key]) {
        assetManagementApiClients[key] = new AssetManagementApiClient(config);
    }
    return assetManagementApiClients[key];
};
class AssetManagementApiClient extends ApiClient {
    /**
     * Request function to send an authenticated POST request to the AssetManagementAPI
     */
    async request(path, options) {
        if (this.config.useMockData) {
            return this.mockRequest(options === null || options === void 0 ? void 0 : options.mockResponse);
        }
        if (this.authClientServiceCalls === null) {
            return Promise.reject("AuthClient not initialized");
        }
        const { method = "POST", headers: additionalHeaders = {}, body: bodyObject } = options || {};
        const headers = Object.assign({ Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${this.token}` }, additionalHeaders);
        const body = bodyObject ? JSON.stringify(bodyObject) : undefined;
        return fetch(`${this.config.assetManagementApiBaseUrl}${path}`, {
            method,
            headers,
            body
        }).then((response) => {
            if (response.status === 401) {
                this.authClientServiceCalls.deleteAccessToken();
                return Promise.reject("Unauthorized");
            }
            if (response.status === 200) {
                return response.json();
            }
            return Promise.reject(`Error ${response.status}`);
        });
    }
}

const createServiceCaseCss = ".mf-create-service-case{display:block}.mf-create-service-case__button-group{display:flex;gap:0 10px;}mf-create-service-case ux-select{width:100%}";
const MfCreateServiceCaseStyle0 = createServiceCaseCss;

const MfCreateServiceCase = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.serviceCaseSubmit = createEvent(this, "mfServiceCaseSubmit", 7);
        this.serviceCaseCancel = createEvent(this, "mfServiceCaseCancel", 7);
        this.apiStore = createApiStore();
        this.authStore = createAuthStore();
        this.formStore = createFormStore();
        this.callId = crypto.randomUUID(); // this browser API only works on localhost and over https
        this.multipleSteps = false;
        this.fileUpload = false;
        this.prefillData = null;
        this.locale = "en-US";
        this.env = "prod";
        this.legacyApi = false;
        this.disableSilentAuth = false;
        this.tbdbError = null;
    }
    connectedCallback() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.publicApiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.publicApiClient.subscribeToTbdbToken((event) => {
            if (event.success) {
                loadFormDataFromPublicApi(this.apiStore, this.publicApiClient);
            }
            else {
                this.tbdbError = event.errorMessage || "Error getting TBDB token";
            }
        });
        this.assetManagementApiClient = getAssetManagementApiClient(this.config);
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuth = whenAuthenticated(() => loadFormDataFromAssetManagementApi(this.apiStore, this.assetManagementApiClient));
        this.apiStore.onChange("notificationTypesLoading", (notificationTypesLoading) => {
            var _a;
            if (!notificationTypesLoading) {
                this.formStore.state.selectedNotificationType = ((_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType) || null;
            }
        });
        document.addEventListener("uxActiveTabSet", (e) => {
            // eslint-disable-next-line no-console
            console.log("uxActiveTabSet", e);
        });
    }
    disconnectedCallback() {
        this.unsubscribeAuth();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }
    onPrefillDataChange() {
        var _a;
        const notificationType = (_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType;
        if (notificationType) {
            this.formStore.state.selectedNotificationType = notificationType;
        }
    }
    loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }
    onSubmit(event) {
        event.preventDefault();
        const { softwares } = this.apiStore.state;
        const { form, callId } = this;
        const data = createRequestData({
            form,
            callId,
            softwares,
            selectedMachineTechnology: this.formStore.state.selectedMachineTechnology
        });
        this.serviceCaseSubmit.emit(data);
        this.formStore.state.formIsSubmitted = true;
        if (this.config.logging) {
            // eslint-disable-next-line no-console
            console.log("Form submitted", data);
        }
    }
    onCancel() {
        this.serviceCaseCancel.emit();
    }
    render() {
        const t = (key, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const { loading } = this.apiStore.state;
        const { isAuthenticating, authError } = this.authStore.state;
        const { prefillData, multipleSteps } = this;
        const { showErrorNumbers, formIsSubmitted, showApplications, showSoftwareModules, showMachines, showSoftwareFields, showWeldingApplicationFields } = this.formStore.state;
        const showContent = !(loading || isAuthenticating || authError);
        return (h(Host, { key: 'e377d18f2e1f5cdd67d3f9c6170d7f1414fc86dd' }, !formIsSubmitted && (h("ux-form", { key: '17eae69ded04ce3b01ebfb5a0674ac3e8caa3f71', "data-validation-required--msg": t("validation.requiredField") }, h(FormSteps, { key: '9d87f667bfe142b4be3d157b1b8c8eb560899ecd', multipleSteps: multipleSteps }, h("form", { key: 'b779dd475237d7eae95dfd535fca73beb59f1428', onSubmit: (e) => this.onSubmit(e), ref: (el) => (this.form = el) }, h("div", { key: '15f4a882331ac7bc72c486f1a13935bcf987a8b1', class: "ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60" }, h("div", { key: '9ea03e55e0355b08abb17ca7fe50ac8b012d8c89', class: "ux-text-headline ux-text-headline--giant" }, t("headline.createCase"))), (loading || isAuthenticating) && (h("ux-loader", { key: '1e05514c60db65bbc8906862e21cd0818e8a591d', class: "ux-u-margin-bottom-40", variant: "spinner" })), authError && (h("div", { key: '8a84337b816bd3ea72457b7fd7e45ae9733b3bd8', class: "ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20" }, t("auth.error"))), h("div", { key: '26ff60859e45f9d6e3bc737045a883aaf625cc7e', style: { display: showContent ? "block" : "none" } }, h(FormStep, { key: 'a54e26d90716ea1199e410b935a2e8aad2c5c09f', multipleSteps: multipleSteps }, h(StepNavigation, { key: 'ce59f3e4b3e1da4f83fc2ff2bb2ac7189fa06188', t: t, multipleSteps: multipleSteps, currentStep: 1 }), h("div", { key: '8a832fd40696588c0eaf94ee703e9158992b8c39', class: "ux-row ux-u-flex-jc-center" }, h("div", { key: 'd4a98f151ad4609451947a916d1111436e6a9383', class: "ux-col-12 ux-col-md-10" }, h("div", { key: '5c0891d710942df986b46b34b9a97a3d9b6ee2cf', class: "ux-row" }, h("div", { key: '82a5eeaec254e0d219c3f88c82bce1a7e1539046', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '0225f7e90ba35d3aab8bcaa993d395f698d81f1d', class: "ux-u-margin-bottom-20" }, h(NotificationTypeSelect, { key: '5ebbc0d18de10f0179aad9c218d961e8fbdbaae6', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showMachines && (h("div", { key: '4f4c10fcfacbaae38ed5b05b0053aa3675e2bee7', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '35fe45bcc70b44d7ffde512621217914a4430a03', class: "ux-u-margin-bottom-20" }, h(MachineSelect, { key: '0b3cc805aca9731086ccde408b2426c0ab2b1a46', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))), showApplications && (h(Fragment, { key: 'ccb6a8518328856d8b36541170aae0d8893d7d4d' }, h("div", { key: '2a9cf3dc481a04ec59b2113ec80b2c1ebd481c85', class: "ux-row" }, h("div", { key: 'b5a12e14db2ffe6825c624a8dbf14c12913535e9', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '8a3961184fcd526369b15c36f809be74593648a9', class: "ux-u-margin-bottom-20" }, h(ApplicationSelect, { key: '13e7723fcf0babc0dbc062429da82b3e1b4d407c', t: t, formStore: this.formStore })))), showWeldingApplicationFields && (h("div", { key: '5d207c4103599f18122d089b1bb407827562600a', class: "ux-row" }, h("div", { key: '7fd059134bab1260c1aee75655616af4e5c11594', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '3a98e5b2db014f9a40d0896c4ecbfb79d7e1d7bc', class: "ux-u-margin-bottom-20" }, h(ApplicationOpticsSelect, { key: 'bcc4dd18a28ff0755213e085cedc513e9253d7fa', t: t }))), h("div", { key: '43ddad868d3de7a2197c5bb6194be53ad859de8d', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '493b8c767b17982a7f1dcbb49b8294ece567ce16', class: "ux-u-margin-bottom-20" }, h(ApplicationIssueSelect, { key: 'a03e087c1eae10138bc156b357ef0ce129a0cbef', t: t }))))))), showSoftwareFields && (h(Fragment, { key: '8a0b5b00e094db87a5dc4352b7b723ea3f0400cf' }, h("div", { key: 'a9f340e6b97337de0492161628e480147889feba', class: "ux-row" }, h("div", { key: 'dfca23d207d0709d12287cf4bcb44adbf905d71c', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '2f0aba633b109aa5cfa2eeee30b633c9a92bd623', class: "ux-u-margin-bottom-20" }, h(SoftwareClassificationSelect, { key: '18ce307e292ed9510f2fd41255678172c85d6f40', t: t, prefillData: prefillData })))), h("div", { key: '4429730a2ce9b371771b7b6aaaa1120f1776541c', class: "ux-row" }, h("div", { key: 'c649bd40ace86830cfd54841e07e69c4b2a38abc', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '93e0b498a29468b5ec714d9dfb29f4303ad6468a', class: "ux-u-margin-bottom-20" }, h(InstallationCheckbox, { key: 'e2bbd7ede4d3a1ee7bdb147432e99c40c50d827e', t: t, prefillData: prefillData }))), h("div", { key: '72bf7b404787e77601d5ec0f82362be67afd88a0', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'd97a8f3d8224a0f4d7ae7932a52e3781a8038f09', class: "ux-u-margin-bottom-20" }, h(LicenseCheckbox, { key: '06f1eaa8cd627db8357c3db92ccc17acc9bcba48', t: t, prefillData: prefillData })))), h("div", { key: '3749362e977dfb4bc0b77fd21ad609c49540dbb7', class: "ux-row" }, h("div", { key: '77a878da9df3b30c16857a213ca5e1f02871ecaf', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'd2f271eb7ccaf9df239ad72c3a02f97f7367e666', class: "ux-u-margin-bottom-20" }, h(SoftwareTypeSelect, { key: '4b366b407a0b4e63e346d72b1fb0675c9d7f64b5', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showSoftwareModules && (h("div", { key: 'f0aede64f8d6a60e897813437ce7eae9fb86ee7a', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'cf1ab742f5a87e35bb56031e068dabff82f8abe3', class: "ux-u-margin-bottom-20" })))), h("div", { key: 'bda154212b9627dc1d93ab9bc5f9f9ba8373ffb2', class: "ux-row" }, showSoftwareModules && (h("div", { key: '8a11e18ea9ba9f7708ddeeec49604b00e5e5ab51', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '00ce3a14f4f4dc0f0e94b9efab74f25a56d8c81c', class: "ux-u-margin-bottom-20" }, h(SoftwareModuleSelect, { key: '5b9e8e12980822c5c470802715d31a53ba4a50be', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))))), showErrorNumbers && (h("div", { key: '0000fccc3c0a580889c2ac92c9440721511c682c', class: "ux-row" }, h("div", { key: 'e1853ad9cfcdc2b0bfce41b49cbb7df0db5b78b8', class: "ux-col-12" }, h(ErrorNumberInput, { key: 'bc388fd3a5172225eb75073709da920319564453', t: t, prefillData: prefillData })))), h("div", { key: 'fdbd1aaecf7c757edac962051a0b015884390045', class: "ux-row" }, h("div", { key: 'c53b3bf06e3053d84a3d17c95f4c2fa2293a4078', class: "ux-col-12" }, h("div", { key: 'f704235b048dd5c130033094b29b76b449d76b13', class: "ux-u-margin-bottom-20" }, h("ux-textarea-field", { key: '728cfe9be138d0dcca931ed6fec2f8dd83810ad3', label: t("field.message.label"), placeholder: t("field.message.placeholder"), required: true, "data-validation-required--msg": t("validation.requiredField"), name: "description" })))), this.fileUpload && (h("div", { key: '05eaba0863470a749a394cd37e2ec8a9cafd5adf', class: "ux-row" }, h("div", { key: '51e03c2c51db38c834dc48f0b1b14c8d4f567421', class: "ux-col-12" }, h("ux-file-upload", { key: 'b25b5d5f7ed36336f91217d6cd7791e16ac3d518', "max-file-size": "2", label: t("field.fileUpload.label"), "accepted-files": "image/*,application/pdf,text/html", "max-files": "4" })))), h(StepNext, { key: 'e73c0c852f9596786a396f0364b59dc1e71f28ec', t: t, multipleSteps: multipleSteps, onCancel: () => this.onCancel() })))), h(FormStep, { key: 'f36db3d12c14a496b831af58ea30624e54605efb', multipleSteps: multipleSteps }, h(StepNavigation, { key: '51e2585f9f146d609a131903cb92250c1422436f', t: t, multipleSteps: multipleSteps, currentStep: 2 }), h("div", { key: 'd0ba528dfb7d384f70fd2555c2cc75f7014d43b8', class: "ux-row ux-u-flex-jc-center" }, h("div", { key: '6ca9ac6e162fe8aeb64cc378706646fa7064f37c', class: "fields-wrapper ux-col-12 ux-col-md-10" }, h("div", { key: '80637eece7bb9d5cf816f7f9d7633a6f67b813bb' }, h("slot", { key: '8e5229f16e3e3bd0baad68217be9aa2255d0dbc2', name: "contacts-button" })), h("div", { key: 'd407f4f5de13813da03e6b2899c9428396416461', class: "ux-row" }, h("div", { key: '8f62cac7204487912f43ad1e000dad6f23b596e6', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'c4d68ef5f61d2a5dd3968fc56f44289b11e9ada4', class: "ux-u-margin-bottom-20" }, h(SalutationInput, { key: 'e76a686da8500ba2c4a051b31e39e3eddb7e13af', t: t, prefillData: prefillData })))), h("div", { key: 'a0619b959f3846c1e018c42a1b7705910e6c9759', class: "ux-row" }, h("div", { key: '91612b1d5e1860d91cbae7d8f97e87e1ed17dd3c', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'ce5ca8febf94ecca41eca8ddc0d8387934d43968', class: "ux-u-margin-bottom-20" }, h(FirstNameInput, { key: '79604628acdfdafa28a29921dab81f8ae9c6b8e6', t: t, prefillData: prefillData }))), h("div", { key: 'ad5fc568b578726a86c26609d70e86be84908114', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'f14462bc8915b85997a768c15e6f062ab13b7fc4', class: "ux-u-margin-bottom-20" }, h(LastNameInput, { key: 'e8db8aaa3c38ce60917db93336292dfe4706cbbf', t: t, prefillData: prefillData })))), h("div", { key: '85a11a8e999c786bdea0f85a64ed734fea66a7f4', class: "ux-row" }, h("div", { key: 'd47f08c241739ec2e09045e6b38d3ec4d1119208', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '43d2c950e08f22f9d3bffe54239a27ab65fdcb1d', class: "ux-u-margin-bottom-20" }, h(PhoneInput, { key: 'aab8bd1c8135e22db4ad33363911bc9b14f156e9', t: t, prefillData: prefillData, locale: this.locale }))), h("div", { key: '0866ab34f1ea0e4306bcda380733d96ad4360765', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '25a1591576329de204768f7ffc7f976cd93b0acf', class: "ux-u-margin-bottom-20" }, h(EmailInput, { key: '33f0741e095ea66972055bfd7033d21af7b2f054', t: t, prefillData: prefillData })))), h("div", { key: '87a3274efb73634d07519f3f0b4cf31a2b96620d', class: "ux-u-margin-bottom-20" }, h("div", { key: '360dc2435ef72ba9b23c34661cfb2729fc1abb1d', class: "ux-text-normal ux-text-normal--light-gray" }, h("p", { key: '7794111bb5973a270043d43771289c150fe9f2cd' }, t("validation.requiredFieldsExplanation")))), h("div", { key: '92be4be5035116cf2f88b711adb791d32482acc8', class: "ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse" }, h("ux-button", { key: '4b4bccb3ade5babeb84cc6e41dba5cca7d2f504b', type: "submit", variant: "primary" }, t("button.submit")), h(StepBack, { key: '77174a86f6dadb047334f275cdf8f3daa730f8fd', t: t, multipleSteps: multipleSteps }), !multipleSteps && (h("ux-button", { key: 'bcee2314f80d060dc7ed950d638d19ec489e4532', type: "button", onClick: () => this.onCancel(), formnovalidate: true }, t("button.cancel")))))))))))), h("div", { key: '8a97ae743bee59baba09b45bc0b9413efe5dcdea', style: { display: formIsSubmitted ? "block" : "none" } }, h("slot", { key: '373c08d8fe0adbc57c470366fec79873dd67eea5', name: "connector" })), h(SilentAuth, { key: 'f7d90ec0bdb996c911757161908ad6ef35098cee', config: this.config, disabled: this.disableSilentAuth, authStore: this.authStore })));
    }
    static get watchers() { return {
        "locale": ["onLocaleChange"],
        "prefillData": ["onPrefillDataChange"]
    }; }
};
MfCreateServiceCase.style = MfCreateServiceCaseStyle0;

export { MfCreateServiceCase as mf_create_service_case };

//# sourceMappingURL=mf-create-service-case.entry.js.map